import React, { useState, useEffect } from "react";
import { useUser } from "../UserContext";
import { useTheme, ThemeProvider, createTheme } from "@mui/material/styles";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { updateUser, updatePricelist } from "../graphql/mutations";
import { listPricelists } from "../graphql/queries";
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  Snackbar,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  IconButton,
  Stack,
  Input,
} from "@mui/material";
import { colors, lighten } from "@mui/material";
import { Auth } from "aws-amplify";
import UserForm from "./user/UpdateForm";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import awsmobile from "../aws-exports";

const SettingsPage = () => {
  const {
    userGroups,
    userRecord,
    setUserRecord,
    providerTheme,
    currentTheme,
    setCurrentTheme,
    setUserTheme,
    setProviderTheme,
  } = useUser();
  const theme = useTheme();
  const [user, setUser] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [logo, setLogo] = useState(userRecord?.companyLogo || null);

  // Initialize theme options from userRecord if available
  const [themeOptions, setThemeOptions] = useState(() => {
    if (userRecord?.themeOptions) {
      try {
        const parsed = JSON.parse(userRecord.themeOptions);
        return {
          primaryColor:
            parsed.primaryColor || parsed.primary || theme.palette.primary.main,
          secondaryColor:
            parsed.secondaryColor ||
            parsed.secondary ||
            theme.palette.secondary.main,
          background:
            parsed.background ||
            parsed.backgroundColor ||
            theme.palette.background.default,
          backgroundPaper:
            parsed.backgroundPaper ||
            parsed.backgroundPaperColor ||
            theme.palette.background.paper,
          textPrimary:
            parsed.textPrimary ||
            parsed.textColor ||
            theme.palette.text.primary,
          textSecondary:
            parsed.textSecondary ||
            parsed.textSecondaryColor ||
            theme.palette.text.secondary,
        };
      } catch (e) {
        console.error("Error parsing theme options:", e);
      }
    }
    return {
      primaryColor: theme.palette.primary.main,
      secondaryColor: theme.palette.secondary.main,
      background: theme.palette.background.default,
      backgroundPaper: theme.palette.background.paper,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
    };
  });

  // Update themeOptions when userRecord changes
  useEffect(() => {
    if (userRecord?.themeOptions) {
      try {
        const parsed = JSON.parse(userRecord.themeOptions);
        setThemeOptions({
          primaryColor:
            parsed.primaryColor || parsed.primary || theme.palette.primary.main,
          secondaryColor:
            parsed.secondaryColor ||
            parsed.secondary ||
            theme.palette.secondary.main,
          background:
            parsed.background ||
            parsed.backgroundColor ||
            theme.palette.background.default,
          backgroundPaper:
            parsed.backgroundPaper ||
            parsed.backgroundPaperColor ||
            theme.palette.background.paper,
          textPrimary:
            parsed.textPrimary ||
            parsed.textColor ||
            theme.palette.text.primary,
          textSecondary:
            parsed.textSecondary ||
            parsed.textSecondaryColor ||
            theme.palette.text.secondary,
        });
        console.log("Loaded theme options from userRecord:", parsed);
      } catch (e) {
        console.error("Error parsing theme options:", e);
      }
    }
  }, [userRecord?.themeOptions]);

  // Generate signed URL for logo display only when needed
  const [displayLogoUrl, setDisplayLogoUrl] = useState(null);

  useEffect(() => {
    const generateLogoUrl = async () => {
      if (logo && logo.includes("logos/")) {
        // This is a file path, generate signed URL
        try {
          const signedUrl = await refreshLogoUrl(logo);
          setDisplayLogoUrl(signedUrl);
        } catch (error) {
          console.error("Error generating signed URL:", error);
          setDisplayLogoUrl(null);
        }
      } else if (logo) {
        // This is already a URL or path
        setDisplayLogoUrl(logo);
      } else {
        setDisplayLogoUrl(null);
      }
    };

    generateLogoUrl();
  }, [logo]); // Only run when logo state changes

  console.log("Settings Debug - userGroups:", userGroups);
  console.log("Settings Debug - providerTheme:", providerTheme);
  console.log("Settings Debug - currentTheme:", currentTheme);
  console.log("Settings Debug - current theme colors:", {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
  });

  const isAdmin = userGroups.includes("admin");
  const isCustomer =
    userRecord &&
    userRecord.priceListInvites &&
    userRecord.priceListInvites.length > 0;

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await Auth.currentAuthenticatedUser();
        setUser(userData);
        // Only set logo if we don't already have one and userRecord has a logo
        if (!logo && userRecord?.companyLogo) {
          // For the initial load, just use the path, don't generate signed URL yet
          setLogo(userRecord.companyLogo);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setSnackbarMessage("Failed to fetch user data. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userRecord]);

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setSnackbarMessage("Passwords do not match.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(
        currentUser,
        currentUser.signInUserSession.accessToken.jwtToken,
        newPassword,
      );
      setSnackbarMessage("Password changed successfully!");
      setSnackbarSeverity("success");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Error changing password:", error);
      setSnackbarMessage(`Error: ${error.message}`);
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.deleteUser(currentUser);
      setSnackbarMessage("Account deleted successfully.");
      setSnackbarSeverity("success");
      // Add any additional logout or redirect logic here
    } catch (error) {
      console.error("Error deleting account:", error);
      setSnackbarMessage(`Error: ${error.message}`);
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
    }
  };

  const handleColorChange = async (colorType, color) => {
    // Map colorType to the correct property name in themeOptions
    const propertyMap = {
      primary: "primaryColor",
      secondary: "secondaryColor",
      background: "background",
      textPrimary: "textPrimary",
    };

    const propertyName = propertyMap[colorType] || colorType;
    const newThemeOptions = {
      ...themeOptions,
      [propertyName]: color,
    };

    // Also set legacy property names for backward compatibility
    if (colorType === "primary") {
      newThemeOptions.primary = color;
    } else if (colorType === "secondary") {
      newThemeOptions.secondary = color;
    } else if (colorType === "background") {
      newThemeOptions.backgroundColor = color;
    } else if (colorType === "textPrimary") {
      newThemeOptions.textColor = color;
    }

    setThemeOptions(newThemeOptions);

    console.log("Updating theme color:", colorType, color);
    console.log("New theme options:", newThemeOptions);

    try {
      // Update the user's theme options
      const updatedUser = await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: userRecord.id,
            themeOptions: JSON.stringify(newThemeOptions),
          },
        }),
      );

      console.log("Updated user record:", updatedUser.data.updateUser);

      // Update the user context
      setUserRecord(updatedUser.data.updateUser);

      // Clear provider theme so user's custom theme takes precedence
      console.log(
        "Clearing provider theme to allow user theme to take precedence",
      );
      setProviderTheme(null);
      localStorage.removeItem("providerTheme");

      // Apply the updated theme using the UserContext function
      setUserTheme(updatedUser.data.updateUser);

      console.log("Theme updated successfully");

      setSnackbarMessage(
        `${colorType.charAt(0).toUpperCase() + colorType.slice(1)} color updated successfully!`,
      );
      setSnackbarSeverity("success");
    } catch (error) {
      console.error(`Error updating ${colorType} color:`, error);
      setSnackbarMessage(`Error updating ${colorType} color: ${error.message}`);
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const fileExtension = file.name.split(".").pop();
      const fileName = `logos/${userRecord.id}-${Date.now()}.${fileExtension}`;

      console.log("Uploading file:", fileName);

      // Upload the file to S3
      await Storage.put(fileName, file, {
        contentType: file.type,
        level: "public",
      });

      // Store just the file path in the database
      const updatedUser = await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: userRecord.id,
            companyLogo: fileName, // Store only the file path
          },
        }),
      );

      // Get a fresh signed URL for display
      const logoUrl = await Storage.get(fileName, {
        level: "public",
        expires: 86400, // URL expires in 24 hours
      });

      console.log("Logo path stored:", fileName);
      console.log("Logo URL for display:", logoUrl);

      // Update the user context with the file path
      setUserRecord(updatedUser.data.updateUser);
      // Update the logo display with the signed URL
      setLogo(logoUrl);

      setSnackbarMessage("Logo uploaded successfully!");
      setSnackbarSeverity("success");
    } catch (error) {
      console.error("Error uploading logo:", error);
      setSnackbarMessage(`Error uploading logo: ${error.message}`);
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  // Add a function to get a fresh signed URL for the logo
  const refreshLogoUrl = async (logoPath) => {
    if (!logoPath) return null;
    try {
      // If the path is already a full URL, extract just the file path
      const pathOnly = logoPath.includes("amazonaws.com")
        ? logoPath.split("/public/").pop()
        : logoPath;

      const url = await Storage.get(pathOnly, {
        level: "public",
        expires: 86400, // URL expires in 24 hours
      });
      return url;
    } catch (error) {
      console.error("Error refreshing logo URL:", error);
      return null;
    }
  };

  const ColorSwatch = ({ color, onClick, selected = false }) => (
    <IconButton
      onClick={onClick}
      sx={{
        width: 40,
        height: 40,
        backgroundColor: color,
        border: selected ? "3px solid" : "1px solid",
        borderColor: selected ? theme.palette.text.primary : "transparent",
        boxShadow: selected
          ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${color}`
          : "none",
        "&:hover": {
          backgroundColor: color,
          borderColor: theme.palette.text.primary,
        },
      }}
    />
  );

  const secondaryColors = [
    // Neon Colors
    "#ff00ff", // Neon Magenta
    "#ff1493", // Deep Pink
    "#ff00cc", // Hot Pink
    "#39ff14", // Neon Green
    "#00ff00", // Lime Green
    "#00ffff", // Cyan
    "#0080ff", // Bright Blue
    "#00bfff", // Deep Sky Blue
    "#ffff00", // Yellow
    "#ffd700", // Gold
    "#ff6600", // Neon Orange
    "#ff4500", // Orange Red
    "#ff073a", // Neon Red
    "#ff0040", // Hot Red
    "#bf00ff", // Electric Purple
    "#8a2be2", // Blue Violet
    "#ff5e00", // Orange
    // Material UI Colors - Reordered by spectrum
    colors.red[300],
    colors.pink[300],
    colors.purple[300],
    colors.deepPurple[300],
    colors.indigo[300],
    colors.blue[300],
    colors.lightBlue[300],
    colors.cyan[300],
    colors.teal[300],
    colors.green[300],
    colors.lightGreen[300],
    colors.lime[300],
    colors.yellow[300],
    colors.amber[300],
    colors.orange[300],
    colors.deepOrange[300],
    colors.brown[300],
    colors.grey[300],
    colors.blueGrey[300],
    "#000", // Black
  ];

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 5 }}>
          User not authenticated. Please log in and try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        backgroundColor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
        pt: 3,
        pb: 3,
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{ mt: 3, color: "text.primary" }}
      >
        Settings
      </Typography>

      <Divider sx={{ mb: 4, borderColor: "primary.main" }} />

      <Stack
        spacing={2}
        sx={{
          backgroundColor: "background.paper",
          p: 3,
          borderRadius: 2,
          border: 1,
          borderColor: "primary.main",
        }}
      >
        <Typography variant="h6" gutterBottom color="text.primary">
          Change Password
        </Typography>

        <TextField
          label="New Password"
          type="password"
          fullWidth
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.palette.text.secondary,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: theme.palette.primary.main,
            },
          }}
        />
        <TextField
          label="Confirm Password"
          type="password"
          fullWidth
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.palette.text.secondary,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: theme.palette.primary.main,
            },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handlePasswordChange}
          sx={{ mt: 1 }}
        >
          Change Password
        </Button>
      </Stack>

      <Divider sx={{ my: 4, borderColor: "primary.main" }} />

      <Stack
        sx={{
          backgroundColor: "background.paper",
          p: 3,
          borderRadius: 2,
          border: 1,
          borderColor: "primary.main",
        }}
      >
        <UserForm user={user} username={user.username} />
      </Stack>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "background.paper",
            border: 1,
            borderColor: "primary.main",
          },
        }}
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteAccount} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SettingsPage;
